import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { BarElement, CategoryScale, Chart, ChartData, ChartOptions, LinearScale, ScriptableContext, Tooltip } from "chart.js";

import { ResilienceTimelineRow, formatScore, getDiagonalPattern } from "pages/app/resilience-score/utils/ResilienceScoreContextDrawerUtils";
import { clampScore } from "pages/app/resilience-score/utils/ResilienceScoreUtils";

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip);

type ResilienceScoreTimelineBarChartProps = {
  rows: ResilienceTimelineRow[];
  maxScore: number;
};

const COLORS = {
  soilHealth: "#f59e0b",
  waterManagement: "#4d85f5",
  biodiversity: "#0d7058",
  unknown: "#d9d9d9",
  grid: "#6d6d6d",
  blackOpacityLight: "#100C06",
  white: "#ffffff",
};

const ResilienceScoreTimelineBarChart = ({ rows, maxScore }: ResilienceScoreTimelineBarChartProps) => {
  const labels = useMemo(() => rows.map((r) => String(r.year)), [rows]);

  const tickLines = useMemo(() => rows.map((r) => [String(r.year), r.unknown ? "–" : formatScore(r.overall, "0.0")]), [rows]);

  const data: ChartData<"bar", Array<number | null>, string> = {
    labels,
    datasets: [
      {
        data: rows.map((r) => (r.unknown ? 0 : clampScore(r.soilHealth, 0))),
        backgroundColor: (context: ScriptableContext<"bar">) => {
          const row = rows[context.dataIndex];
          if (!row || !row.unknown) return COLORS.soilHealth;
          return getDiagonalPattern(context.chart.ctx, context.chart.chartArea);
        },
        borderRadius: (context: ScriptableContext<"bar">) => (rows[context.dataIndex]?.unknown ? 0 : 6),
        borderSkipped: false,
      },
      {
        data: rows.map((r) => (r.unknown ? 0 : clampScore(r.waterManagement, 0))),
        backgroundColor: (context: ScriptableContext<"bar">) => {
          const row = rows[context.dataIndex];
          if (!row || !row.unknown) return COLORS.waterManagement;
          return getDiagonalPattern(context.chart.ctx, context.chart.chartArea);
        },
        borderRadius: (context: ScriptableContext<"bar">) => (rows[context.dataIndex]?.unknown ? 0 : 6),
        borderSkipped: false,
      },
      {
        data: rows.map((r) => (r.unknown ? 0 : clampScore(r.biodiversity, 0))),
        backgroundColor: (context: ScriptableContext<"bar">) => {
          const row = rows[context.dataIndex];
          if (!row || !row.unknown) return COLORS.biodiversity;
          return getDiagonalPattern(context.chart.ctx, context.chart.chartArea);
        },
        borderRadius: (context: ScriptableContext<"bar">) => (rows[context.dataIndex]?.unknown ? 0 : 6),
        borderSkipped: false,
      },
      {
        data: rows.map((r) => (r.unknown ? maxScore : null)),
        backgroundColor: (context) => getDiagonalPattern(context.chart.ctx, context.chart.chartArea),
        borderRadius: 6,
        borderSkipped: false,
        grouped: false,
        barPercentage: 1.0,
        categoryPercentage: 0.8,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        yAlign: "bottom",
        xAlign: "center",
        backgroundColor: COLORS.white,
        borderColor: COLORS.unknown,
        borderWidth: 1,
        padding: 6,
        bodyColor: COLORS.blackOpacityLight,
        displayColors: false,
        bodyFont: {
          weight: 500,
          family: "dazzed",
          size: 12,
        },
        filter: (tooltipItem) => !rows[tooltipItem.dataIndex]?.unknown,
        callbacks: {
          title: () => "",
          label: (context) => formatScore(Number(context.parsed.y), "0.0"),
        },
      },
    },
    layout: {
      padding: {
        left: -8,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    scales: {
      x: {
        stacked: false,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          font: {
            family: "dazzed",
            size: 12,
            weight: 500,
            lineHeight: 1.5,
          },
          color: COLORS.grid,
          labelOffset: -25,
          callback: (_value, index) => tickLines[index] ?? "",
        },
      },
      y: {
        stacked: false,
        min: 0,
        max: maxScore,
        ticks: {
          stepSize: 2,
          color: COLORS.grid,
          font: {
            family: "dazzed",
            size: 10,
            weight: 500,
          },
          padding: 8,
        },
        grid: {
          color: COLORS.unknown,
          drawTicks: false,
        },
        border: {
          display: false,
        },
      },
    },
  };

  return <Bar data={data} options={options} data-testid="resilience-score-timeline-bar-chart" />;
};

export default ResilienceScoreTimelineBarChart;
